import React, { useEffect, useState, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, PageHeader, Form } from 'antd'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { errorMessage, successMessage } from 'services/messager'
import {
	getOptions,
	getSolicitantAreasOptions,
	createWorkflow,
	createDemand,
} from 'services/demands/demandsServices'
import { getCompanyTeams } from 'services/demands/teamsServices'
import FormSection from './components/FormSection'
import { mimeTypes } from 'utils/file'
import { priorities } from 'utils/constants'
import FieldForm from './components/fieldForm'

import { StyledForm, StyledFormItem } from './styles'
import styles from './index.module.scss'
import FormFactory from 'components/formFactory'
import useTemplateVarsForm from './hooks/useTemplateVarsForm'
import { appendAnswer } from 'states/modules/answer'
import { nextPage, previousPage } from 'states/modules/question'
import { getAnalysisObjectsOptions } from 'services/demands/demandsServices'

const ALLCARE_COMPANY_ID =
	Number(process.env?.REACT_APP_ALLCARE_COMPANY_ID) || 1

const CreateDemand = () => {
	const history = useHistory()
	const queryClient = useQueryClient()
	const dispatch = useDispatch()
	const [templateVariablesForm] = Form.useForm()

	const user = useSelector(({ session }) => session)
	const answer = useSelector(({ answer }) => answer)

	const { control, handleSubmit, watch, setValue, reset, trigger } = useForm({
		defaultValues: {
			taskName: '',
			description: '',
			categoryId: undefined,
			templateId: undefined,
			priority: undefined,
			fileList: [],
			solicitantAreaId: undefined,
			management_team_id: undefined,
			legal_team_id: undefined,
			technical_area_team_id: undefined,
			diretoria_team_id: undefined,
			optionalRoute: undefined,
		},
	})

	const [currentStep, setCurrentStep] = useState(0)
	const [moduleTooltip, setModuleTooltip] = useState('')
	const [requireTemplateFields, setRequireTemplateFields] = useState(false)
	const [isUploadTemplate, setIsUploadTemplate] = useState(false)

	const watchValues = watch()

	const taskNameLabel =
		user.company_id === ALLCARE_COMPANY_ID
			? 'Título do chamado'
			: 'Fornecedor/Contraparte'
	const isAllCareUser = user.company_id === ALLCARE_COMPANY_ID

	const { loading: isLoadingTemplateVarsForm } = useTemplateVarsForm(
		watchValues.templateId
	)

	const { data: categories, isLoading: isLoadingCategories } = useQuery({
		queryKey: ['category-options'],
		queryFn: async () => {
			try {
				const data = await getOptions({
					route: '/categories/list',
					page: 1,
					limit: 100,
				})

				return data.list
			} catch {
				errorMessage({ content: 'Erro ao obter categorias' })
			}
		},
	})

	const { data: models, isLoading: isLoadingModels } = useQuery({
		queryKey: ['models-options'],
		queryFn: async () => {
			try {
				const data = await getOptions({
					route: '/templates/list',
					page: 1,
					limit: 100,
				})
				return data.list
			} catch {
				errorMessage({ content: 'Erro ao obter modelos' })
			}
		},
	})

	const { data: solicitantAreas, isLoading: isLoadingAreas } = useQuery({
		queryKey: ['solicitant-areas-options'],
		queryFn: () => getSolicitantAreasOptions(''),
	})

	const { data: teams, isLoading: isLoadingTeams } = useQuery({
		queryKey: ['teams-options'],
		queryFn: async () => {
			try {
				const data = await getCompanyTeams(1, 100)
				return data.list
			} catch {
				errorMessage({ content: 'Erro ao obter times' })
			}
		},
	})

	const { data: analysisObjects, isLoading: isLoadingAnalysisObjects } =
		useQuery({
			queryKey: ['analysis-objects-options'],
			queryFn: async () => {
				try {
					const data = await getAnalysisObjectsOptions(1, 100)
					return data || []
				} catch {
					errorMessage({ content: 'Erro ao obter objetos de análise' })
				}
			},
		})

		const { mutate: newDemand, isPending: isLoadingDemand } = useMutation({
		mutationFn: async (values) => {

			console.log("Valores: ", values)
			const workflowFormData = new FormData()
			const workflowPayload = {
				taskName: values.taskName,
				priority: values.priority,
				categoryId: values.categoryId,
				description: values.description,
				solicitantAreaId: values.solicitantAreaId,
				managementAreaId: values.managementAreaId,
				analysisObjectId: values?.analysisObjectId || null,
				templateId: values.templateId,
				variables: values.variables,
			}

			Object.entries(workflowPayload).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					workflowFormData.append(key, value)
				}
			})

			if (values.fileList?.length > 0) {
				values.fileList.forEach((file) => {
					const actualFile = file.originFileObj ? file.originFileObj : file
					workflowFormData.append('files', actualFile)
				})
			}

			if (watchValues.categoryId === externalMinuta?.id) {
				return await createWorkflow(workflowFormData)
			} else {
				return await createDemand(workflowFormData)
			}
		},
		onSuccess: () => {
			successMessage({ content: 'Nova demanda cadastrada com sucesso!' })
			reset()
			queryClient.invalidateQueries({ queryKey: ['demands'] })
			history.push('/demands')
		},
		onError: (error) => {
			console.error(error)
			errorMessage({
				content: error.message || 'Erro ao cadastrar nova demanda.',
			})
		},
	})

	const onSubmit = (values) => newDemand(values)

	const handleGoBack = () => {
		reset()
		history.push('/demands')
	}

	useEffect(() => {
		if (!models || !watchValues.templateId) return
		const template = models.find((m) => m.id === watchValues.templateId)
		setModuleTooltip(template?.config?.description ?? '')
	}, [models, watchValues.templateId])

	const handleCategoryChange = (value, fieldOnChange) => {
		const found = categories?.find((c) => c.id === value)
		setRequireTemplateFields(Boolean(found?.require_template))
		setValue('templateId', undefined)
		fieldOnChange(value)
	}

	const handleTemplateChange = (value, fieldOnChange) => {
		const found = models?.find((t) => t.id === value)
		setIsUploadTemplate(found?.text_type === 'upload')
		fieldOnChange(value)
	}

	const enrichedWatchValues = {
		...watchValues,
		_requireTemplate: requireTemplateFields,
		_hasModuleTooltip: Boolean(moduleTooltip),
		_moduleTooltip: moduleTooltip,
	}

	const externalMinuta = useMemo(
		() => categories?.find((c) => !!c?.has_workflow),
		[categories]
	)
	
	const step0Fields = [
		{
			type: 'select',
			name: 'solicitantAreaId',
			label: 'Área Solicitante',
			required: true,
			placeholder: 'Selecione a área solicitante',
			options: solicitantAreas?.filter((area) => !area.onlyManagement),
			isLoading: isLoadingAreas,
			allowClear: true,
			rules: { required: 'Selecione uma área solicitante' },
		},
		{
			type: 'input',
			name: 'taskName',
			label: taskNameLabel,
			required: true,
		},
		{
			type: 'textarea',
			name: 'description',
			label: isAllCareUser
				? 'Descrição sobre as condições técnicas, comerciais e negociais'
				: 'Descrição',
			required: true,
		},
		{
			type: 'select',
			name: 'categoryId',
			label: 'Categoria',
			required: true,
			options: categories,
			isLoading: isLoadingCategories,
			rules: { required: 'Selecione uma categoria' },
			onChange: handleCategoryChange,
		},
		{
			type: 'select',
			name: 'managementAreaId',
			label: 'Área Gestora do Contrato',
			required: true,
			placeholder: 'Selecione a área gestora',
			options: solicitantAreas?.filter((area) => area.managementTeamId),
			isLoading: isLoadingAreas,
			allowClear: true,
			rules: { required: 'Selecione uma área gestora' },
			attacheds: [
				[{ key: 'categoryId', values: [externalMinuta?.id ?? null] }],
			],
		},
		{
			type: 'select',
			name: 'templateId',
			label: 'Modelo',
			required: true,
			options: models,
			isLoading: isLoadingModels,
			rules: { required: 'Selecione um modelo' },
			onChange: handleTemplateChange,
			attacheds: [[{ key: '_requireTemplate', values: [true] }]],
		},
		{
			type: 'alert',
			name: '_moduleTooltipAlert',
			alertType: 'info',
			alertMessage: 'Informações do modelo',
			alertDescription: (vals) => vals._moduleTooltip || '',
			attacheds: [
				[
					{ key: '_requireTemplate', values: [true] },
					{ key: '_hasModuleTooltip', values: [true] },
				],
			],
		},
		{
			type: 'select',
			name: 'priority',
			label: 'Prioridade',
			required: true,
			options: priorities,
			rules: { required: 'Selecione a prioridade' },
		},
		...(isAllCareUser
			? [
					{
						type: 'select',
						name: 'analysisObjectId',
						label: 'Objeto das Análises',
						required: true,
						options: analysisObjects,
						isLoading: isLoadingAnalysisObjects,
						rules: { required: 'Selecione o objeto de análise' },
					},
				]
			: []),
		{
			type: 'uploader',
			name: 'fileList',
			label: 'Anexos',
			required: isAllCareUser,
			multiple: true,
			max: 5,
			allowedTypes: Object.keys(mimeTypes),
			allowedExtensions: Object.values(mimeTypes),
		},
	]

	const hasPreview = false

	const { questions, lastPage } = useSelector(
		({ question }) => ({
			questions: question.data,
			lastPage: hasPreview ? question.lastPage + 1 : question.lastPage,
		}),
		shallowEqual
	)

	const visible = useSelector(({ question }) => question.visible)
	const currentPage = useSelector(({ question }) => question.currentPage)
	const isLastPage = currentPage === lastPage

	const handleNextStep = async () => {
		const isValid = await trigger()
		if (!isValid) return

		if (requireTemplateFields && !isUploadTemplate) {
			setCurrentStep(1)
			return
		}

		handleSubmit(onSubmit)()
	}

	const handleBackNavigation = () => {
		if (currentStep === 1) {
			if (currentPage > 0) {
				dispatch(previousPage())
			} else {
				setCurrentStep(0)
			}
		}
	}

	const onNext = async () => {
		try {
			await templateVariablesForm.validateFields()

			dispatch(
				appendAnswer({
					data: templateVariablesForm.getFieldsValue(),
					pageFieldsData: questions[currentPage],
					visible: visible[currentPage],
				})
			)

			if (!isLastPage) {
				dispatch(nextPage({ hasPreview }))
			} else {
				handleSubmit((values) =>
					onSubmit({ ...values, variables: JSON.stringify(answer.data) })
				)()
			}
		} catch (e) {}
	}

	const isStep0Valid = useMemo(() => {
		const requiredFields = step0Fields.filter((field) => {
			if (field.type === 'alert') return false
			if (!field.required) return false
			if (field.attacheds) {
				const attachment = field.attacheds[0]
				if (attachment) {
					return attachment.some((cond) => {
						const fieldValue = watchValues[cond.key]
						return cond.values.includes(fieldValue)
					})
				}
			}
			return true
		})

		return requiredFields.every((field) => {
			const value = watchValues[field.name]
			if (field.type === 'uploader') {
				return Array.isArray(value) && value.length > 0
			}
			return value !== undefined && value !== null && value !== ''
		})
	}, [watchValues])

	return (
		<div className={styles.layout}>
			<PageHeader
				title="Nova Demanda"
				onBack={handleGoBack}
				className={styles.header}
			/>

			<div className={styles.content}>
				<StyledForm layout="vertical">
					{currentStep === 0 && (
						<div className={styles.formSection}>
							<FormSection
								title="Informações da demanda"
								description="Preencha cuidadosamente as informações da demanda
								para que o responsável jurídico tenha um entendimento claro sobre
								a solicitação."
							/>

							{step0Fields.map((fieldConfig) => (
								<FieldForm
									key={fieldConfig.name}
									config={fieldConfig}
									control={control}
									setValue={setValue}
									watchValues={enrichedWatchValues}
								/>
							))}
						</div>
					)}

					{currentStep === 1 && watchValues.templateId && !isUploadTemplate && (
						<div className={styles.formSection}>
							<FormSection
								title="Informações do documento"
								description="Preencha com as informações necessárias para elaboração do contrato."
							/>
							<FormFactory form={templateVariablesForm} />
						</div>
					)}

					<div className={styles.footer}>
						{currentStep === 0 ? (
							<Button
								type="primary"
								onClick={handleNextStep}
								loading={isLoadingDemand}
								disabled={!isStep0Valid}>
								{requireTemplateFields && !isUploadTemplate
									? 'Próxima etapa'
									: 'Cadastrar demanda'}
							</Button>
						) : (
							<div style={{ display: 'flex', gap: 8 }}>
								<Button onClick={handleBackNavigation}>Voltar</Button>
								<Button
									type="primary"
									onClick={onNext}
									loading={isLoadingDemand}>
									{isLastPage ? 'Cadastrar demanda' : 'Próxima etapa'}
								</Button>
							</div>
						)}
					</div>
				</StyledForm>
			</div>
		</div>
	)
}

export default CreateDemand
